import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  ApartmentOutlined,
  ArrowForward,
  CheckCircleOutline,
  Refresh,
  StorageOutlined,
  WarningAmber,
  WorkspacePremiumOutlined,
} from '@mui/icons-material';
import api, { integradoresAPI, instanciasAPI } from '../api';
import { useStore } from '../store';
import { useAuth } from '../hooks/useAuth';
import type { Catalog, Subscription } from '../commercial/types';
import PlanChip from '../components/PlanChip';

type PodStatus = { status: string; ready?: boolean; reason?: string };
type PodInfo = { backend?: PodStatus; frontend?: PodStatus };
const podRunning = (pod?: PodStatus) =>
  pod?.status === 'Running' && pod.ready !== false;
const podLabel = (pod?: PodStatus) =>
  !pod
    ? 'Não encontrado'
    : pod.ready === false
    ? pod.reason || 'Não pronto'
    : pod.status;
const labels: Record<string, string> = {
  ativo: 'Ativa',
  criando: 'Em configuração',
  erro: 'Com erro',
  deletado: 'Excluída',
};
const states: Record<string, string> = {
  active: 'Ativas',
  trial: 'Em avaliação',
  suspended: 'Suspensas',
  cancelled: 'Canceladas',
  expired: 'Encerradas',
};
const colors: Record<string, string> = {
  ativo: '#2e7d32',
  criando: '#1976d2',
  erro: '#d84315',
  deletado: '#78909c',
};
const cardStyle = { height: '100%', borderColor: '#e0e6ef', borderRadius: 2 };

export default function Dashboard() {
  const {
    integradores,
    instancias,
    loading,
    error: baseError,
    setIntegradores,
    setInstancias,
    setError,
  } = useStore();
  const { can } = useAuth();
  const commercialAccess = can('commercial.manage');
  const [pods, setPods] = useState<Record<string, PodInfo> | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[] | null>(
    null
  );
  const [error, setSummaryError] = useState(''),
    [refreshing, setRefreshing] = useState(false),
    [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const requestId = useRef(0);
  const refresh = useCallback(
    async (refreshBase = false) => {
      const request = ++requestId.current;
      setRefreshing(true);
      const jobs: { key: string; promise: Promise<any> }[] = [
        { key: 'pods', promise: instanciasAPI.k8sInfo() },
      ];
      if (commercialAccess)
        jobs.push(
          { key: 'catalog', promise: api.get('/commercial/catalog') },
          {
            key: 'subscriptions',
            promise: api.get('/commercial/subscriptions'),
          }
        );
      if (refreshBase)
        jobs.push(
          { key: 'integradores', promise: integradoresAPI.listar() },
          { key: 'instancias', promise: instanciasAPI.listar() }
        );
      const results = await Promise.allSettled(jobs.map((job) => job.promise));
      if (request !== requestId.current) return;
      const failures: string[] = [];
      results.forEach((result, index) => {
        const key = jobs[index].key;
        if (result.status === 'rejected') {
          failures.push(
            (
              {
                pods: 'pods',
                catalog: 'catálogo',
                subscriptions: 'assinaturas',
                integradores: 'integradores',
                instancias: 'instâncias',
              } as Record<string, string>
            )[key]
          );
          if (key === 'pods') setPods(null);
          if (key === 'catalog') setCatalog(null);
          if (key === 'subscriptions') setSubscriptions(null);
          return;
        }
        const data = result.value.data;
        if (key === 'pods') setPods(data);
        if (key === 'catalog') setCatalog(data);
        if (key === 'subscriptions') setSubscriptions(data);
        if (key === 'integradores') setIntegradores(data.data);
        if (key === 'instancias') setInstancias(data.data);
      });
      if (refreshBase)
        setError(
          failures.includes('integradores') || failures.includes('instâncias')
            ? 'Não foi possível atualizar os cadastros.'
            : null
        );
      setSummaryError(
        failures.length
          ? 'Não foi possível atualizar: ' +
              failures.join(', ') +
              '. Tente novamente.'
          : ''
      );
      setUpdatedAt(new Date());
      setRefreshing(false);
    },
    [commercialAccess, setIntegradores, setInstancias, setError]
  );
  useEffect(() => {
    void refresh();
    return () => {
      requestId.current++;
    };
  }, [refresh]);

  const current = instancias.filter(
    (instance) => instance.status !== 'deletado'
  );
  const operationalIssue = (instance: (typeof current)[number]) =>
    instance.status === 'erro' ||
    (instance.status === 'ativo' &&
      pods !== null &&
      (!instance.namespace_k8s ||
        !podRunning(pods[instance.namespace_k8s]?.backend) ||
        !podRunning(pods[instance.namespace_k8s]?.frontend)));
  const issues = current.filter(operationalIssue);
  const active = current.filter(
    (instance) => instance.status === 'ativo'
  ).length;
  const withoutSubscription = integradores.filter(
    (integrator) => !integrator.commercialPlan
  ).length;
  const pendingSync = subscriptions?.filter(
    (subscription) => subscription.syncStatus !== 'applied'
  ).length;
  const stats = [
    {
      label: 'Integradores',
      value: integradores.length,
      note: 'Cadastros na plataforma',
      icon: <ApartmentOutlined />,
      color: '#1976d2',
    },
    {
      label: 'Instâncias ativas',
      value: active,
      note: 'Situação registrada no Management',
      icon: <StorageOutlined />,
      color: '#2e7d32',
    },
    {
      label: 'Precisam de atenção',
      value: issues.length,
      note: pods
        ? 'Erros de cadastro ou pods fora de execução'
        : 'Erros de cadastro; pods ainda não consultados',
      icon: <WarningAmber />,
      color: issues.length ? '#d84315' : '#607d8b',
    },
    {
      label: commercialAccess ? 'Assinaturas atribuídas' : 'Em configuração',
      value: commercialAccess
        ? subscriptions?.length ?? '—'
        : current.filter((instance) => instance.status === 'criando').length,
      note: commercialAccess
        ? 'Contratações no novo catálogo'
        : 'Provisionamentos em andamento',
      icon: <WorkspacePremiumOutlined />,
      color: '#6a4ca5',
    },
  ];
  const recent = [...integradores]
    .sort(
      (a, b) =>
        new Date(b.data_criacao).getTime() - new Date(a.data_criacao).getTime()
    )
    .slice(0, 5);
  const overview = [...current]
    .sort(
      (a, b) =>
        Number(operationalIssue(b)) - Number(operationalIssue(a)) ||
        new Date(b.data_criacao).getTime() - new Date(a.data_criacao).getTime()
    )
    .slice(0, 6);
  const links = [
    { label: 'Integradores', to: '/integradores' },
    { label: 'Instâncias e logs', to: '/instancias' },
    ...(commercialAccess
      ? [{ label: 'Planos e módulos', to: '/comercial' }]
      : []),
    ...(can('financial.manage')
      ? [{ label: 'Financeiro', to: '/faturamento' }]
      : []),
    ...(can('users.manage')
      ? [{ label: 'Usuários', to: '/management-users' }]
      : []),
    ...(can('infra.manage')
      ? [{ label: 'Infraestrutura', to: '/infraestrutura' }]
      : []),
  ];
  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Dashboard
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Operação e contratação das suas instâncias.
          </Typography>
        </Box>
        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
          <Button
            startIcon={
              refreshing ? <CircularProgress size={16} /> : <Refresh />
            }
            variant="outlined"
            disabled={refreshing || loading}
            onClick={() => void refresh(true)}
          >
            Atualizar visão geral
          </Button>
          {updatedAt && (
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Última consulta:{' '}
              {updatedAt.toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
          )}
        </Box>
      </Stack>
      {(baseError || error) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {baseError}
          {baseError && error ? ' ' : ''}
          {error}
        </Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat) => (
          <Grid item xs={12} sm={6} lg={3} key={stat.label}>
            <Card variant="outlined" sx={cardStyle}>
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                  <Avatar
                    sx={{
                      bgcolor: stat.color + '12',
                      color: stat.color,
                      width: 38,
                      height: 38,
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                </Stack>
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 700, mt: 1, color: stat.color }}
                >
                  {loading ? '—' : stat.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stat.note}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={7} sx={{ minWidth: 0 }}>
          <Card variant="outlined" sx={cardStyle}>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Typography variant="h6" fontWeight={700}>
                  Situação das instâncias
                </Typography>
                <Chip size="small" label={current.length + ' instância(s)'} />
              </Stack>
              {loading ? (
                <LinearProgress />
              ) : current.length === 0 ? (
                <Typography color="text.secondary">
                  Nenhuma instância cadastrada.
                </Typography>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      height: 10,
                      borderRadius: 2,
                      overflow: 'hidden',
                      mb: 2,
                    }}
                  >
                    {['ativo', 'criando', 'erro'].map((status) => {
                      const count = current.filter(
                        (instance) => instance.status === status
                      ).length;
                      return count > 0 ? (
                        <Box
                          key={status}
                          title={labels[status] + ': ' + count}
                          sx={{
                            width: (count / current.length) * 100 + '%',
                            bgcolor: colors[status],
                          }}
                        />
                      ) : null;
                    })}
                  </Box>
                  <Stack
                    direction="row"
                    useFlexGap
                    flexWrap="wrap"
                    spacing={2}
                    sx={{ mb: 2 }}
                  >
                    {['ativo', 'criando', 'erro'].map((status) => (
                      <Typography key={status} variant="body2">
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            bgcolor: colors[status],
                            borderRadius: '50%',
                            mr: 1,
                          }}
                        />
                        {labels[status]}:{' '}
                        {
                          current.filter(
                            (instance) => instance.status === status
                          ).length
                        }
                      </Typography>
                    ))}
                  </Stack>
                  {issues.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {issues.length} instância(s) precisam de atenção. Confira
                      os logs antes de executar uma ação.
                    </Alert>
                  )}
                  {overview.map((instance, index) => {
                    const integrator = integradores.find(
                      (item) => item._id === instance.integrador_id
                    );
                    const info = instance.namespace_k8s
                      ? pods?.[instance.namespace_k8s]
                      : null;
                    return (
                      <Box
                        key={instance._id}
                        sx={{
                          py: 1.5,
                          borderTop: index ? 1 : 0,
                          borderColor: 'divider',
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography fontWeight={600}>
                              {integrator?.empresa || instance.nome}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {integrator?.alias || instance.namespace_k8s}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={labels[instance.status] || instance.status}
                            color={
                              instance.status === 'ativo'
                                ? 'success'
                                : instance.status === 'erro'
                                ? 'error'
                                : 'info'
                            }
                            variant="outlined"
                          />
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.75 }}
                        >
                          {pods === null
                            ? 'Consulta dos pods indisponível ou em andamento.'
                            : info
                            ? 'API: ' +
                              podLabel(info.backend) +
                              ' · Interface: ' +
                              podLabel(info.frontend)
                            : 'Pods não encontrados para esta instância.'}
                        </Typography>
                        {instance.status === 'erro' && instance.logs && (
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{ mt: 0.5 }}
                          >
                            {instance.logs}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </>
              )}
              <Button
                component={Link}
                to="/instancias"
                endIcon={<ArrowForward />}
                sx={{ mt: 1 }}
              >
                Ver instâncias e logs
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={5} sx={{ minWidth: 0 }}>
          <Card variant="outlined" sx={cardStyle}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                {commercialAccess ? 'Contratação e módulos' : 'Acessos rápidos'}
              </Typography>
              {commercialAccess ? (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    A versão da aplicação é independente do plano. Os módulos e
                    limites passam a valer quando uma assinatura é atribuída e
                    sincronizada.
                  </Alert>
                  <Stack spacing={1.5}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Typography variant="body2">
                        Sem assinatura atribuída
                      </Typography>
                      <Typography fontWeight={700}>
                        {loading ? '—' : withoutSubscription}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Typography variant="body2">
                        Sincronizações pendentes ou com falha
                      </Typography>
                      <Typography
                        fontWeight={700}
                        color={pendingSync ? 'warning.main' : 'text.primary'}
                      >
                        {pendingSync ?? '—'}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  {subscriptions === null ? (
                    <Typography variant="body2" color="text.secondary">
                      Assinaturas ainda não consultadas.
                    </Typography>
                  ) : subscriptions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma assinatura no novo catálogo. As instâncias mantêm
                      o acesso existente.
                    </Typography>
                  ) : (
                    <Stack
                      direction="row"
                      useFlexGap
                      flexWrap="wrap"
                      spacing={1}
                    >
                      {Object.entries(states).map(([key, label]) => {
                        const count = subscriptions.filter(
                          (subscription) => subscription.effectiveStatus === key
                        ).length;
                        return count ? (
                          <Chip
                            key={key}
                            size="small"
                            label={label + ': ' + count}
                          />
                        ) : null;
                      })}
                    </Stack>
                  )}
                  {catalog && (
                    <Box
                      sx={{ mt: 2, p: 2, bgcolor: '#f5f7fb', borderRadius: 1 }}
                    >
                      <Typography variant="body2">
                        Catálogo:{' '}
                        {catalog.plans.filter((plan) => plan.published).length}{' '}
                        de {catalog.plans.length} planos publicados.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Trial:{' '}
                        {catalog.settings.trialEnabled
                          ? 'habilitado'
                          : 'desativado'}{' '}
                        · {catalog.settings.trialDays} dias.
                      </Typography>
                    </Box>
                  )}
                  <Button
                    component={Link}
                    to="/comercial"
                    endIcon={<ArrowForward />}
                    sx={{ mt: 2 }}
                  >
                    Gerenciar planos e assinaturas
                  </Button>
                </>
              ) : (
                <Alert
                  icon={<CheckCircleOutline />}
                  severity="info"
                  sx={{ mb: 2 }}
                >
                  Consulte as instâncias e seus logs. As tarefas disponíveis
                  respeitam as permissões do seu perfil.
                </Alert>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Navegar no Management
              </Typography>
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                {links.map((link) => (
                  <Button
                    key={link.to}
                    component={Link}
                    to={link.to}
                    variant="outlined"
                    size="small"
                  >
                    {link.label}
                  </Button>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card variant="outlined" sx={cardStyle}>
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Typography variant="h6" fontWeight={700}>
                  Integradores recentes
                </Typography>
                <Button
                  component={Link}
                  to="/integradores"
                  size="small"
                  endIcon={<ArrowForward />}
                >
                  Ver todos
                </Button>
              </Stack>
              {loading ? (
                <LinearProgress />
              ) : recent.length === 0 ? (
                <Typography color="text.secondary">
                  Nenhum integrador cadastrado.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {recent.map((integrator) => (
                    <Stack
                      key={integrator._id}
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.5}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                    >
                      <Avatar
                        src={
                          integrator.company_branding_logo_url ||
                          integrator.logo_url
                        }
                        sx={{ bgcolor: '#e8edf7', color: '#2454a6' }}
                      >
                        {integrator.empresa?.slice(0, 1)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={600}>
                          {integrator.empresa}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {integrator.email}
                        </Typography>
                      </Box>
                      <Box>
                        <PlanChip
                          plan={integrator.plano}
                          commercialName={integrator.commercialPlan?.name}
                        />
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          Cadastro em{' '}
                          {new Date(integrator.data_criacao).toLocaleDateString(
                            'pt-BR'
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
